import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useState, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { PasswordInput } from '@/components/forms/password-input'
import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { PageHeader } from '@/components/layout/page-header'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { IconButton } from '@/components/primitives/icon-button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { clearPortalSession } from '@/lib/auth/portal-session'
import {
  persistPortalLoginPrefillEmail,
  readStoredPortalAccount,
  updateStoredPortalAccount,
  type StoredPortalAccount,
} from '@/lib/auth/session-storage'
import { cn } from '@/lib/cn'

import { updateAdminPassword, updateAdminProfile, type AdminProfile } from './api'
import { resolveAdminProfileErrorMessage } from './errors'

type AdminProfileTab = 'basic' | 'security' | 'usage'

interface PasswordFormState {
  confirmPassword: string
  currentPassword: string
  newPassword: string
}

type PasswordErrors = Partial<Record<keyof PasswordFormState, string>>

function CloseIcon() {
  return (
    <svg aria-hidden className="size-5" fill="none" viewBox="0 0 20 20">
      <path
        d="m5 5 10 10M15 5 5 15"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg aria-hidden className="size-3.5" fill="none" viewBox="0 0 16 16">
      <path
        d="M9.7 3.1 12.9 6.3M2.7 10.9 10.9 2.7a1.7 1.7 0 0 1 2.4 2.4l-8.2 8.2-3.1.7.7-3.1Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.35"
      />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="m6 4 4 4-4 4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

function getProfileInitial(profile: Pick<AdminProfile, 'email' | 'name'>) {
  const source = profile.name.trim() || profile.email.trim()

  return Array.from(source)[0]?.toUpperCase() ?? 'A'
}

function normalizeStoredAdminProfile(account: StoredPortalAccount): AdminProfile | null {
  const id = account.id?.trim()
  const email = account.email?.trim()

  if (!id || !email) {
    return null
  }

  const storedName = account.name?.trim()
  let name = email

  if (storedName) {
    name = storedName
  }

  return {
    email,
    id,
    name,
    role: account.role,
    status: account.status,
  }
}

export function AdminProfilePage() {
  const { t } = useTranslation('adminProfile')
  const navigate = useNavigate()
  const [profile, setProfile] = useState(() =>
    normalizeStoredAdminProfile(readStoredPortalAccount('admin')),
  )

  return (
    <AdminShellPage activePath="/admin/profile">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader
          actions={
            <IconButton
              aria-label={t('actions.close')}
              className="size-9 text-[var(--color-text-muted)]"
              onClick={() => {
                void navigate({ to: '/admin/overview' })
              }}
              size="sm"
            >
              <CloseIcon />
            </IconButton>
          }
          contentClassName="max-w-[800px]"
          title={t('title')}
        />
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-2 pb-8 lg:px-[30px]">
          {profile ? (
            <AdminProfileWorkspace
              key={`${profile.id}:${profile.name}`}
              onProfileChange={setProfile}
              profile={profile}
            />
          ) : (
            <PageContentBoundary className="flex min-h-[360px] max-w-[800px] items-center justify-center">
              <InlineNotice role="alert" tone="danger">
                {t('loadErrorDescription')}
              </InlineNotice>
            </PageContentBoundary>
          )}
        </div>
      </div>
    </AdminShellPage>
  )
}

function AdminProfileWorkspace({
  onProfileChange,
  profile,
}: {
  onProfileChange: (profile: AdminProfile) => void
  profile: AdminProfile
}) {
  const { t } = useTranslation('adminProfile')
  const { showFeedback } = useFeedback()
  const [activeTab, setActiveTab] = useState<AdminProfileTab>('basic')
  const [nameEditorOpen, setNameEditorOpen] = useState(false)
  const [name, setName] = useState(profile.name)
  const [nameError, setNameError] = useState('')

  const syncProfile = (nextProfile: AdminProfile) => {
    onProfileChange(nextProfile)
    updateStoredPortalAccount('admin', {
      email: nextProfile.email,
      id: nextProfile.id,
      name: nextProfile.name,
      role: nextProfile.role ?? undefined,
      status: nextProfile.status ?? undefined,
    })
  }

  const saveProfileMutation = useMutation({
    mutationFn: updateAdminProfile,
    onError: (error) => {
      showFeedback({
        message: resolveAdminProfileErrorMessage(error, t),
        tone: 'error',
      })
    },
    onSuccess: (nextProfile) => {
      syncProfile(nextProfile)
      setNameEditorOpen(false)
      showFeedback({
        message: t('success.profileSaved'),
        tone: 'success',
      })
    },
  })

  const selectTab = (tab: AdminProfileTab) => {
    setActiveTab(tab)
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
  }

  const openNameEditor = () => {
    setName(profile.name)
    setNameError('')
    setNameEditorOpen(true)
  }

  const closeNameEditor = () => {
    setName(profile.name)
    setNameError('')
    setNameEditorOpen(false)
  }

  const submitName = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()

    const normalizedName = name.trim()
    if (!normalizedName) {
      setNameError(t('validation.nameRequired'))
      window.requestAnimationFrame(() => {
        document.getElementById('admin-profile-display-name')?.focus()
      })
      return
    }

    setNameError('')
    saveProfileMutation.mutate({ adminId: profile.id, name: normalizedName })
  }

  return (
    <PageContentBoundary className="grid max-w-[800px] gap-4">
      <ProfileTabs activeTab={activeTab} onSelectTab={selectTab} />

      <div className="grid gap-4">
        <section className="rounded-xl border border-[#ededee] bg-white px-5 py-5 sm:px-6">
          <div className="flex min-w-0 items-center gap-4">
            <AvatarDisplay initial={getProfileInitial(profile)} />

            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <h2 className="truncate text-base font-bold text-[var(--color-text)]">
                  {profile.name || t('fallbackName')}
                </h2>
                <IconButton
                  aria-label={t('nameEditor.edit')}
                  className="size-7 shrink-0 text-[#6b7280] hover:text-[var(--color-text)]"
                  onClick={openNameEditor}
                  size="sm"
                >
                  <PencilIcon />
                </IconButton>
              </div>
              <p className="mt-1 truncate text-xs text-[#6b7280]">{profile.email}</p>
            </div>
          </div>

          <NameEditorModal
            closeLabel={t('nameEditor.close')}
            error={nameError}
            isSaving={saveProfileMutation.isPending}
            name={name}
            onChange={(nextName) => {
              setName(nextName)
              if (nameError) {
                setNameError('')
              }
            }}
            onClose={closeNameEditor}
            onSubmit={submitName}
            open={nameEditorOpen}
            title={t('nameEditor.title')}
          />
        </section>

        <PasswordCard adminId={profile.id} profileEmail={profile.email} />
      </div>
    </PageContentBoundary>
  )
}

function AvatarDisplay({ initial }: { initial: string }) {
  return (
    <div className="flex size-[54px] shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] text-[30px] leading-none text-white">
      {initial}
    </div>
  )
}

function ProfileTabs({
  activeTab,
  onSelectTab,
}: {
  activeTab: AdminProfileTab
  onSelectTab: (tab: AdminProfileTab) => void
}) {
  const { t } = useTranslation('adminProfile')
  const tabs: AdminProfileTab[] = ['basic']

  return (
    <div
      aria-label={t('tabs.label')}
      className="flex h-[60px] items-start gap-6 border-b border-[#ededee] bg-white pt-[17px]"
      role="tablist"
    >
      {tabs.map((tab) => (
        <button
          aria-selected={activeTab === tab}
          className={cn(
            'relative h-7 cursor-pointer px-0 text-sm font-normal transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
            activeTab === tab
              ? 'text-[var(--color-primary)]'
              : 'text-[var(--color-text)] hover:text-[var(--color-primary)]',
          )}
          key={tab}
          onClick={() => onSelectTab(tab)}
          role="tab"
          type="button"
        >
          {t(`tabs.${tab}`)}
          {activeTab === tab ? (
            <span className="absolute right-0 bottom-0 left-0 h-0.5 bg-[var(--color-primary)]" />
          ) : null}
        </button>
      ))}
    </div>
  )
}

function NameEditorModal({
  closeLabel,
  error,
  isSaving,
  name,
  onChange,
  onClose,
  onSubmit,
  open,
  title,
}: {
  closeLabel: string
  error: string
  isSaving: boolean
  name: string
  onChange: (name: string) => void
  onClose: () => void
  onSubmit: (event: SyntheticEvent<HTMLFormElement>) => void
  open: boolean
  title: string
}) {
  const { t } = useTranslation('adminProfile')

  return (
    <Modal
      className="max-w-[430px]"
      closeLabel={closeLabel}
      footer={
        <>
          <Button disabled={isSaving} onClick={onClose} variant="secondary">
            {t('nameEditor.cancel')}
          </Button>
          <Button form="admin-profile-name-form" isLoading={isSaving} type="submit">
            {t('nameEditor.submit')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={title}
    >
      <form className="grid gap-4" id="admin-profile-name-form" onSubmit={onSubmit}>
        <FormField
          error={error}
          htmlFor="admin-profile-display-name"
          label={t('fields.displayName')}
          required
        >
          <Input
            id="admin-profile-display-name"
            invalid={Boolean(error)}
            onChange={(event) => onChange(event.target.value)}
            placeholder={t('nameEditor.placeholder')}
            value={name}
          />
        </FormField>
      </form>
    </Modal>
  )
}

function PasswordCard({ adminId, profileEmail }: { adminId: string; profileEmail: string }) {
  const { t } = useTranslation('adminProfile')
  const [passwordEditorOpen, setPasswordEditorOpen] = useState(false)

  return (
    <section className="overflow-hidden rounded-xl border border-[#ededee] bg-white">
      <h3 className="px-4 py-3 text-sm font-bold text-[var(--color-text)] sm:px-5">
        {t('password.title')}
      </h3>

      <button
        className="flex min-h-12 w-full items-center justify-between border-t border-[rgba(26,28,25,0.06)] px-4 text-left text-sm text-[var(--color-text)] transition-colors hover:bg-[#f8f8f8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[var(--color-focus)] sm:px-5"
        onClick={() => setPasswordEditorOpen(true)}
        type="button"
      >
        <span>{t('password.reset')}</span>
        <span className="text-[#6b7280]">
          <ChevronRightIcon />
        </span>
      </button>

      <PasswordEditorModal
        adminId={adminId}
        closeLabel={t('password.close')}
        onClose={() => setPasswordEditorOpen(false)}
        open={passwordEditorOpen}
        profileEmail={profileEmail}
        title={t('password.modalTitle')}
      />
    </section>
  )
}

function PasswordEditorModal({
  adminId,
  closeLabel,
  onClose,
  open,
  profileEmail,
  title,
}: {
  adminId: string
  closeLabel: string
  onClose: () => void
  open: boolean
  profileEmail: string
  title: string
}) {
  const { t } = useTranslation('adminProfile')
  const { showFeedback } = useFeedback()
  const [passwordForm, setPasswordForm] = useState<PasswordFormState>({
    confirmPassword: '',
    currentPassword: '',
    newPassword: '',
  })
  const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({})

  const resetPasswordForm = () => {
    setPasswordForm({
      confirmPassword: '',
      currentPassword: '',
      newPassword: '',
    })
    setPasswordErrors({})
  }

  const closeAndReset = () => {
    resetPasswordForm()
    onClose()
  }

  const passwordMutation = useMutation({
    mutationFn: updateAdminPassword,
    onError: (error) => {
      showFeedback({
        message: resolveAdminProfileErrorMessage(error, t),
        tone: 'error',
      })
    },
    onSuccess: () => {
      showFeedback({
        message: t('success.passwordChanged'),
        tone: 'success',
      })
      persistPortalLoginPrefillEmail('admin', profileEmail)
      clearPortalSession('admin')
      window.location.assign('/admin/login')
    },
  })

  const updatePasswordField = (field: keyof PasswordFormState, value: string) => {
    setPasswordForm((current) => ({ ...current, [field]: value }))
    setPasswordErrors((current) => ({ ...current, [field]: undefined }))
  }

  const validatePassword = () => {
    const nextErrors: PasswordErrors = {}

    if (!passwordForm.currentPassword.trim()) {
      nextErrors.currentPassword = t('validation.currentPasswordRequired')
    }

    if (passwordForm.newPassword.length < 8) {
      nextErrors.newPassword = t('validation.newPasswordLength')
    }

    if (passwordForm.confirmPassword !== passwordForm.newPassword) {
      nextErrors.confirmPassword = t('validation.confirmPasswordMismatch')
    }

    setPasswordErrors(nextErrors)

    const firstField = Object.keys(nextErrors)[0] as keyof PasswordFormState | undefined
    if (firstField) {
      window.requestAnimationFrame(() => {
        document.getElementById(`admin-profile-${firstField}`)?.focus()
      })
    }

    return Object.keys(nextErrors).length === 0
  }

  const submitPassword = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validatePassword()) {
      return
    }

    passwordMutation.mutate({
      adminId,
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    })
  }

  return (
    <Modal
      className="max-w-[430px]"
      closeLabel={closeLabel}
      footer={
        <>
          <Button disabled={passwordMutation.isPending} onClick={closeAndReset} variant="secondary">
            {t('password.cancel')}
          </Button>
          <Button
            form="admin-profile-password-form"
            isLoading={passwordMutation.isPending}
            type="submit"
          >
            {t('password.submit')}
          </Button>
        </>
      }
      onClose={closeAndReset}
      open={open}
      title={title}
    >
      <div className="grid gap-4">
        <InlineNotice className="py-2.5" tone="warning">
          {t('password.reloginNotice')}
        </InlineNotice>

        <form className="grid gap-4" id="admin-profile-password-form" onSubmit={submitPassword}>
          <FormField
            error={passwordErrors.currentPassword}
            htmlFor="admin-profile-currentPassword"
            label={t('fields.currentPassword')}
            required
          >
            <PasswordInput
              autoComplete="current-password"
              hideLabel={t('password.hidePassword')}
              id="admin-profile-currentPassword"
              invalid={Boolean(passwordErrors.currentPassword)}
              onChange={(event) => updatePasswordField('currentPassword', event.target.value)}
              showLabel={t('password.showPassword')}
              value={passwordForm.currentPassword}
            />
          </FormField>
          <FormField
            error={passwordErrors.newPassword}
            htmlFor="admin-profile-newPassword"
            label={t('fields.newPassword')}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              hideLabel={t('password.hidePassword')}
              id="admin-profile-newPassword"
              invalid={Boolean(passwordErrors.newPassword)}
              onChange={(event) => updatePasswordField('newPassword', event.target.value)}
              showLabel={t('password.showPassword')}
              value={passwordForm.newPassword}
            />
          </FormField>
          <FormField
            error={passwordErrors.confirmPassword}
            htmlFor="admin-profile-confirmPassword"
            label={t('fields.confirmPassword')}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              hideLabel={t('password.hidePassword')}
              id="admin-profile-confirmPassword"
              invalid={Boolean(passwordErrors.confirmPassword)}
              onChange={(event) => updatePasswordField('confirmPassword', event.target.value)}
              showLabel={t('password.showPassword')}
              value={passwordForm.confirmPassword}
            />
          </FormField>
        </form>
      </div>
    </Modal>
  )
}
